import List from '@pelckmans/elementis/components/list';
import ChapterIcon from '@pelckmans/elementis/icons/chapter';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ModuleContext from '../../_contexts/ModuleContext';
import { SearchResultMedialink } from './../api/search-api';
import ModalContext from '../../../../contexts/ModalContext';
import useMedialinks from '../../toc/hooks/medialink/useMedialinks';
import Pill from '@pelckmans/elementis/components/pill';
import { getFullHierarchyDescriptionOfNode } from '../../../../utils/getNodeDescription';
import { MaterialIcon } from '../../components/MaterialIcon';
import Avatar from '@pelckmans/elementis/components/avatar';
import AvatarImage from '@pelckmans/elementis/components/avatar/avatar-image';
import { getMedialinkById } from '../../../../api/medialink';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../constants/analyticsEvents';
import { buildGroupProps } from '../../../../utils/elementisUtils';
import useLicenses from '../../../../hooks/useLicenses';
import stripHtml from '../../../../utils/wordlist/stripHtml';
import MaterialPills from '../../toc/components/MaterialPills';

interface Props {
  searchItem: SearchResultMedialink;
}

function SearchResultMedialinkItem({ searchItem }: Props) {
  const { allModules } = useContext(ModuleContext);
  const { showModal } = useContext(ModalContext);
  const { t } = useTranslation();
  const { openMedialink } = useMedialinks();
  const { isModuleLicensed } = useLicenses();

  const targetModule = allModules.find(m => m.id === searchItem.moduleId);

  const handleOpenMedialink = useCallback(
    async (searchItem: SearchResultMedialink) => {
      const medialink = await getMedialinkById(searchItem.moduleId, searchItem.id);
      if (!medialink || !targetModule) return;

      if (!searchItem.hierarchy || searchItem.hierarchy.length === 0)
        throw new Error('Node ID is missing for the medialink');

      const node = searchItem.hierarchy[searchItem.hierarchy.length - 1];

      const modal = await openMedialink(
        medialink,
        node,
        targetModule,
        undefined,
        undefined,
        ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_SEARCH,
      );

      if (modal) {
        showModal(modal);
      }
    },
    [targetModule, openMedialink, showModal],
  );

  if (!targetModule) return null;
  const licensedModule = isModuleLicensed(targetModule);

  const content = searchItem.content ? searchItem.content : undefined;

  const { hierarchy } = searchItem;
  return (
    <List.Item
      key={searchItem.id}
      as="button"
      title={!licensedModule ? stripHtml(searchItem.name) : searchItem.name}
      subText={content}
      disabled={!licensedModule}
      onClick={() => {
        handleOpenMedialink(searchItem);
      }}
      pillGroupProps={buildGroupProps(
        <>
          <Pill key="scope-pill">{t('PAGES.SEARCH.RESULTS.MATERIAL')}</Pill>
          <Pill
            key="module-pill"
            size="sm"
            avatar={
              <Avatar>
                <AvatarImage size="xs" name={targetModule!.niceName} src={targetModule!.styling?.logo || ''} />
              </Avatar>
            }
          >
            {targetModule?.niceName}
          </Pill>
          <Pill key="hierarchy-pill" icon={<ChapterIcon />}>
            {getFullHierarchyDescriptionOfNode(hierarchy, t)}
          </Pill>
          <Pill
            key="content-type-pill"
            icon={<MaterialIcon materialType="medialink" contentType={searchItem.contentType} size="xs" />}
          >
            {searchItem.contentType.name}
          </Pill>
          <MaterialPills
            key="material-pills"
            isShared={searchItem.shared}
            isAssignment={searchItem.isAssignment}
            isInReview={searchItem.inReview}
          />
        </>,
      )}
    />
  );
}

export default SearchResultMedialinkItem;
